export type AuthErrorCode =
  | 'email_taken'
  | 'recaptcha_failed'
  | 'invalid_credentials'
  | 'unauthenticated'
  | 'network'
  | 'server'
  | 'unknown'
  | 'invalid_google_token'
  | 'google_email_not_verified'
  | 'google_login_disabled'
  | 'use_google_login'
  | 'password_setup_required'
  | 'restaurant_not_found'
  | 'invalid_reset_token'
  | 'ordering_not_configured';

export type AuthFieldErrors = Record<string, string[]>;

export class AuthApiError extends Error {
  code: AuthErrorCode;
  status: number;
  body?: unknown;
  fieldErrors?: AuthFieldErrors;

  constructor(
    code: AuthErrorCode,
    status: number,
    body?: unknown,
    fieldErrors?: AuthFieldErrors,
  ) {
    super(`AuthApiError: ${code} (HTTP ${status})`);
    this.name = 'AuthApiError';
    this.code = code;
    this.status = status;
    this.body = body;
    this.fieldErrors = fieldErrors ?? normalizeAuthFieldErrors(body);
  }
}

export interface ConsumerPayload {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  google_connected?: boolean;
  google_email?: string | null;
}

export interface RegisterRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  restaurant_uuid: string;
  recaptcha_token: string;
}

export interface RegisterResponse {
  user_type: 'consumer';
  token: string;
  consumer: ConsumerPayload;
}

export interface LoginRequest {
  email: string;
  password: string;
  // Optional: when present, ignite-hub upserts a ConsumerRestaurantRelation row
  // so the restaurant's admin Client page lists this consumer.
  restaurant_uuid?: string;
}

export interface LoginResponse {
  message: string;
  user_type: 'consumer';
  uuid: string;
  token: string;
  consumer: ConsumerPayload;
}

export interface GetMeResponse {
  consumer: ConsumerPayload;
}

function getHubBase(): string {
  const url = import.meta.env.VITE_HUB_URL;
  if (!url) throw new AuthApiError('network', 0);
  return url.replace(/\/$/, '');
}

export function normalizeAuthFieldErrors(body: unknown): AuthFieldErrors | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return undefined;

  const fieldErrors: AuthFieldErrors = {};
  for (const [field, value] of Object.entries(body)) {
    if (typeof value === 'string' && value.trim()) {
      fieldErrors[field] = [value];
    } else if (Array.isArray(value)) {
      const messages = value.filter((message): message is string => (
        typeof message === 'string' && message.trim().length > 0
      ));
      if (messages.length > 0) fieldErrors[field] = messages;
    }
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

async function parseError(res: Response, fallback: AuthErrorCode): Promise<AuthApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }

  const errStr = JSON.stringify(body ?? '');

  // SC-777: the account exists but was created with Google, so it has no
  // password. Hub returns this marker on /register (400) — never on /login,
  // which has no captcha and would become an enumeration oracle. The register
  // form turns it into "use the Google button" instead of asking again for a
  // password that does not exist. Checked before the status branches below,
  // which would otherwise read it as email_taken.
  if (/use_google_login/.test(errStr)) {
    return new AuthApiError('use_google_login', res.status, body);
  }

  // SC-781: same shape, no Google behind it — the account was carried over
  // from the restaurant's previous ordering site and has never had a password.
  // Nothing typed on this form proves the mailbox is the sender's, so the only
  // way in is the link mailed to it. Checked here for the same reason as above:
  // the status branches below would read it as email_taken.
  if (/password_setup_required/.test(errStr)) {
    return new AuthApiError('password_setup_required', res.status, body);
  }
  if (res.status === 503 && /google_login_disabled/.test(errStr)) {
    return new AuthApiError('google_login_disabled', res.status, body);
  }
  if (res.status === 404 && /restaurant_not_found/.test(errStr)) {
    return new AuthApiError('restaurant_not_found', res.status, body);
  }
  if (res.status === 401) {
    if (/invalid_token|invalid_issuer/.test(errStr)) {
      return new AuthApiError('invalid_google_token', res.status, body);
    }
    if (/email_not_verified/.test(errStr)) {
      return new AuthApiError('google_email_not_verified', res.status, body);
    }
    return new AuthApiError(
      fallback === 'invalid_credentials' ? 'invalid_credentials' : 'unauthenticated',
      res.status,
      body,
    );
  }
  if (res.status === 400 && /recaptcha/i.test(errStr)) {
    return new AuthApiError('recaptcha_failed', res.status, body);
  }
  if (res.status === 400 && /email/i.test(errStr)) {
    return new AuthApiError('email_taken', res.status, body);
  }
  if (res.status >= 500) {
    return new AuthApiError('server', res.status, body);
  }
  return new AuthApiError(fallback, res.status, body);
}

export async function registerConsumer(req: RegisterRequest): Promise<RegisterResponse> {
  const base = getHubBase();
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/consumer/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  } catch {
    throw new AuthApiError('network', 0);
  }

  if (!res.ok) throw await parseError(res, 'unknown');
  return res.json() as Promise<RegisterResponse>;
}

export async function loginConsumer(req: LoginRequest): Promise<LoginResponse> {
  const base = getHubBase();
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/consumer/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  } catch {
    throw new AuthApiError('network', 0);
  }

  if (!res.ok) throw await parseError(res, 'invalid_credentials');
  return res.json() as Promise<LoginResponse>;
}

export async function getMeConsumer(token: string): Promise<GetMeResponse> {
  const base = getHubBase();
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/consumer/getMe/`, {
      method: 'GET',
      headers: { Authorization: `Token ${token}` },
    });
  } catch {
    throw new AuthApiError('network', 0);
  }

  if (!res.ok) throw await parseError(res, 'unauthenticated');
  return res.json() as Promise<GetMeResponse>;
}

export interface GoogleLoginRequest {
  id_token: string;
  restaurant_uuid: string;
}

export async function loginConsumerWithGoogle(req: GoogleLoginRequest): Promise<LoginResponse> {
  const base = getHubBase();
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/consumer/google/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  } catch {
    throw new AuthApiError('network', 0);
  }
  if (!res.ok) throw await parseError(res, 'unknown');
  return res.json() as Promise<LoginResponse>;
}

export async function updateMeConsumer(
  token: string,
  patch: Partial<Pick<ConsumerPayload, 'phone' | 'first_name' | 'last_name'>>,
): Promise<{ consumer: ConsumerPayload }> {
  const base = getHubBase();
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/consumer/updateMe/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
      body: JSON.stringify(patch),
    });
  } catch {
    throw new AuthApiError('network', 0);
  }
  if (!res.ok) throw await parseError(res, 'unauthenticated');
  return res.json() as Promise<{ consumer: ConsumerPayload }>;
}

// --- SC-778: forgot / set password -----------------------------------------

export interface ForgotPasswordRequest {
  email: string;
  restaurant_uuid: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  restaurant_uuid?: string;
}

export interface ResetPasswordResponse {
  user_type: 'consumer';
  uuid: string;
  token: string;
  consumer: ConsumerPayload;
}

/** Read the `code` ignite-hub puts in the body, if there is one. */
function bodyCode(body: unknown): string {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const code = (body as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

/**
 * Ask ignite-hub to mail a link letting this address set a password.
 *
 * The server answers the same whether or not the address is registered — it
 * must not become a way to test who eats where — so a resolved promise means
 * "the request was accepted", never "an account exists".
 */
export async function forgotConsumerPassword(req: ForgotPasswordRequest): Promise<void> {
  const base = getHubBase();
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/consumer/forgot-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  } catch {
    throw new AuthApiError('network', 0);
  }

  if (res.ok) return;

  let body: unknown;
  try { body = await res.json(); } catch { body = undefined; }
  const code = bodyCode(body);
  if (code === 'ORDERING_URL_NOT_CONFIGURED') {
    throw new AuthApiError('ordering_not_configured', res.status, body);
  }
  if (code === 'RESTAURANT_NOT_FOUND') {
    throw new AuthApiError('restaurant_not_found', res.status, body);
  }
  throw new AuthApiError(res.status >= 500 ? 'server' : 'unknown', res.status, body);
}

/**
 * Set the password the mailed link authorises. The response is a session: the
 * client who just proved he reads that mailbox is signed in on the spot rather
 * than being sent back to a login form to retype what he just chose.
 */
export async function resetConsumerPassword(
  req: ResetPasswordRequest,
): Promise<ResetPasswordResponse> {
  const base = getHubBase();
  let res: Response;
  try {
    res = await fetch(`${base}/accounts/consumer/reset-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  } catch {
    throw new AuthApiError('network', 0);
  }

  if (res.ok) return res.json() as Promise<ResetPasswordResponse>;

  let body: unknown;
  try { body = await res.json(); } catch { body = undefined; }
  if (bodyCode(body) === 'INVALID_TOKEN') {
    throw new AuthApiError('invalid_reset_token', res.status, body);
  }
  // Anything else at 400 is field validation (a password the server refuses);
  // AuthApiError already lifts those into fieldErrors.
  throw new AuthApiError(res.status >= 500 ? 'server' : 'unknown', res.status, body);
}
